import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { IoCloudDownloadOutline, IoSearch, IoPencilOutline } from 'react-icons/io5';
import { IniciativaFormatada } from 'types/iniciativa';
import { useAuth } from 'context/AuthContext';
import { useActivePhases } from 'hooks/useControlDate';
import { fetchTodasSelecoesByUser } from 'services/selecao-etapa3.service';
import { buscarIniciativasEtapa3 } from 'services/iniciativa.service';
import { getLimiteVotosEtapa3, isAssessorEtapa3 } from 'types/etapa3';
import { HeaderElbb } from '../HomeElbb/components';
import { IniciativaCardEtapa3 as IniciativaCard } from '../HomePublic/components/IniciativaCardEtapa3';
import {
  CATEGORIAS_PARTICIPANTES_ETAPA3,
} from '../HomeElbb/components/CategoriasParticipantesEtapa3';
import './style.scss';

type VotosMap = Record<string, string[]>;

export function MeusVotosEtapa3() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { etapa3premio } = useActivePhases();
  const [isLoading, setIsLoading] = useState(true);
  const [votos, setVotos] = useState<VotosMap>({});
  const [iniciativasPorCategoria, setIniciativasPorCategoria] = useState<
    Record<string, IniciativaFormatada[]>
  >({});
  const [busca, setBusca] = useState('');

  const limite = useMemo(() => getLimiteVotosEtapa3(user ?? null), [user]);
  const isCompactLayout = limite === 1;

  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;

    async function carregar() {
      try {
        const selecoesTodas = await fetchTodasSelecoesByUser(user!._id);
        if (cancelled) return;

        const idsElbb = new Set(CATEGORIAS_PARTICIPANTES_ETAPA3.map((c) => c.id));
        const selecoes = selecoesTodas.filter((s) => idsElbb.has(s.categoria));

        const votosMap: VotosMap = {};
        selecoes.forEach((s) => { votosMap[s.categoria] = s.iniciativaIds; });
        setVotos(votosMap);

        const todasIniciativas = await buscarIniciativasEtapa3(user!._id);
        if (cancelled) return;

        const mapa: Record<string, IniciativaFormatada[]> = {};
        selecoes.forEach((selecao) => {
          const idsVotados = new Set(selecao.iniciativaIds);
          mapa[selecao.categoria] = todasIniciativas.filter((i) => idsVotados.has(i.id));
        });
        setIniciativasPorCategoria(mapa);
      } catch (err) {
        console.error('[MeusVotosEtapa3] Erro ao carregar votos:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    carregar();
    return () => { cancelled = true; };
  }, [user?._id]);

  const categoriasExibidas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return CATEGORIAS_PARTICIPANTES_ETAPA3
      .map((categoria) => {
        const lista = (iniciativasPorCategoria[categoria.id] || []).filter(
          (iniciativa) => termo.length === 0 || iniciativa.titulo.toLowerCase().includes(termo)
        );
        return { categoria, lista };
      })
      .filter((entry) => entry.lista.length > 0);
  }, [busca, iniciativasPorCategoria]);

  const handleExportarPdf = useCallback(async () => {
    const itensParaExportar: Array<{
      titulo: string;
      descricao: string;
      resultado: string;
      videoUrl?: string;
      categoria: string;
      tema?: string;
      categoryPremio?: string;
    }> = [];

    Object.values(iniciativasPorCategoria).forEach((lista) => {
      lista.forEach((iniciativa) => {
        itensParaExportar.push({
          titulo: iniciativa.titulo,
          descricao: iniciativa.descricao || '',
          resultado: iniciativa.resultado || '',
          videoUrl: iniciativa.originalVideoUrl || iniciativa.videoUrl,
          categoria: iniciativa.categoria,
          tema: iniciativa.tema || '',
          categoryPremio: iniciativa.categoryPremio || '',
        });
      });
    });

    if (itensParaExportar.length > 0) {
      const { exportarIniciativasPdf } = await import('utils/votacao-etapa1-export');
      exportarIniciativasPdf(
        itensParaExportar,
        'meus-votos-etapa3-todas-categorias',
        'Meus votos no Prêmio ELBB 2026'
      );
    }
  }, [iniciativasPorCategoria]);

  const semVotos = Object.values(votos).every((arr) => !arr || arr.length === 0);

  // Assessor não tem voto próprio na Etapa 3 — só prepara o preliminar do VP.
  // Redireciona para /votacao-etapa-3 (que mostra a tela travada com o preliminar).
  if (isAssessorEtapa3(user ?? null)) {
    return <Navigate to="/votacao-etapa-3" replace />;
  }

  return (
    <div id="meus-votos-etapa-3-wrapper">
      <HeaderElbb currentPage="inicio" />

      <section className="meus-votos-etapa-3-hero">
        <div className="meus-votos-etapa-3-hero__content">
          <h1 className="meus-votos-etapa-3-hero__title">
            <span className="meus-votos-etapa-3-hero__title--pink">Meus votos no </span>
            <span className="meus-votos-etapa-3-hero__title--yellow">Prêmio ELBB 2026</span>
            <br />
            <span className="meus-votos-etapa-3-hero__title--cyan">
              Confira aqui as iniciativas que você votou
            </span>
          </h1>
        </div>
      </section>

      <main className="meus-votos-etapa-3-main">
        <div className="meus-votos-etapa-3-toolbar">
          <h2 className="meus-votos-etapa-3-toolbar__title">Esses foram os seus votos!</h2>

          <div className="meus-votos-etapa-3-toolbar__filters">
            <div className="meus-votos-etapa-3-toolbar__search">
              <input
                type="search"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="O que você procura?"
                className="meus-votos-etapa-3-toolbar__search-input"
                aria-label="Buscar iniciativa"
              />
              <IoSearch
                className="meus-votos-etapa-3-toolbar__search-icon"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <div className="meus-votos-etapa-3-actions">
          {etapa3premio && (
            <button
              type="button"
              className="meus-votos-etapa-3-actions__btn meus-votos-etapa-3-actions__btn--outline"
              onClick={() => navigate('/votacao-etapa-3')}
            >
              <IoPencilOutline size={18} aria-hidden="true" />
              EDITAR
            </button>
          )}
          <button
            type="button"
            className="meus-votos-etapa-3-actions__btn"
            onClick={handleExportarPdf}
          >
            <IoCloudDownloadOutline size={18} aria-hidden="true" />
            EXPORTAR PDF
          </button>
        </div>

        {isLoading && (
          <p className="meus-votos-etapa-3-empty">Carregando seus votos...</p>
        )}

        {!isLoading && semVotos && (
          <p className="meus-votos-etapa-3-empty">
            Você ainda não enviou nenhum voto na Etapa 3.
          </p>
        )}

        {!isLoading && !semVotos && categoriasExibidas.length === 0 && (
          <p className="meus-votos-etapa-3-empty">
            Nenhuma iniciativa encontrada para os filtros selecionados.
          </p>
        )}

        <div
          className={`meus-votos-etapa-3-categorias${
            isCompactLayout ? ' meus-votos-etapa-3-categorias--compact' : ''
          }`}
        >
          {categoriasExibidas.map(({ categoria, lista }) => (
            <section className="meus-votos-etapa-3-categoria" key={categoria.id}>
              <header className="meus-votos-etapa-3-categoria__header">
                <img
                  src={categoria.imagem}
                  alt=""
                  aria-hidden="true"
                  className="meus-votos-etapa-3-categoria__icon"
                />
                <h3 className="meus-votos-etapa-3-categoria__title">
                  {categoria.nome}
                </h3>
              </header>

              <div className="meus-votos-etapa-3-categoria__grid">
                {lista.map((iniciativa) => (
                  <IniciativaCard
                    key={iniciativa.id}
                    {...iniciativa}
                    votar={{
                      votada: true,
                      readonly: true,
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
